<script module lang="ts">
  // get either PreviewBaseProps or henkes simple preview props

  const getSimplePreview = (customSticker: CustomStickerWithId, adminPreview: boolean = false) => {
    return fetch(`${stickerPreviewContext.STICKERAPP_API_URL}/sheet/getCustomStickerPreview`, {
      body: JSON.stringify({ ...customSticker, adminPreview }),
      method: 'POST',
    })
      .then((response) => response.json())
      .then((data) => {
        if (
          Value.Check(
            Type.Object({
              preview: Type.String(),
              livePreviewAvailable: Type.Boolean(),
            }),
            data,
          )
        ) {
          return data;
        }
        return undefined;
      });
  };

  function handlePartSide(part: Part) {
    /* For some reason part.side does not exist. Part meta seem to always be present. Why? This is needed for correct bleed*/
    if (!part.side) {
      if (part.meta?.SIDE) {
        part.side = part.meta.SIDE;
      }
    }
  }

  const patternScale = 0.5;

  async function fetchAndProcessMasks(
    productItem: ProductItemInterface,
  ): Promise<WhiteLayerMask[]> {
    const opacityMaskID = productItem?.attributes['live_preview_opacity_mask']?.toString();
    const whiteLayerMaskID = productItem?.attributes['live_preview_white_layer_mask']?.toString();

    if (!opacityMaskID || !whiteLayerMaskID) {
      return [];
    }

    const params = new URLSearchParams({
      opacityLayer: opacityMaskID || '',
      whiteLayer: whiteLayerMaskID || '',
    });

    try {
      const maskfilesres = await fetch(`/editor/get_masks?${params.toString()}`);
      const masks: WhiteLayerMask[] = (await maskfilesres.json()) as WhiteLayerMask[];
      return masks.length > 0 ? masks : [];
    } catch (error) {
      console.log('Failed to load masks:', error);
      return [];
    }
  }

  const getLivePreviewProps = async (productItem: ProductItemInterface, parts: Array<Part>) => {
    const materialSettings = materialOptions.find(
      (material) => material.type === productItem?.attributes['material'],
    );
    const metallicOpacity =
      typeof materialSettings?.metallicOpacity === 'function'
        ? materialSettings.metallicOpacity(productItem?.attributes['laminate'] as string)
        : (materialSettings?.metallicOpacity ?? 0);

    return Promise.all(
      (['front', 'back'] as const).map(
        async (side): Promise<['front' | 'back', PreviewBaseProps | undefined]> => {
          parts.forEach((p) => handlePartSide(p));
          let frontPart = parts.find((part) => part.side === side);
          if (!frontPart) {
            return [side, undefined];
          }

          let realsvg: string;
          const features = Array.isArray(productItem.attributes['feature'])
            ? (productItem.attributes['feature'] as string[])
            : [];
          // Extract the backpart and only use that for generating svg
          // NOTE: generateSvgStringFromParts ignores backpart
          if (side === 'back' && features.includes('backpaper_print')) {
            const backPart = parts.filter((x) => x.side === 'back')[0];
            if (!backPart) {
              throw new Error('Back part not found in parts array');
            }
            realsvg = SVGUtils.generateSvgStringFromPart(backPart);
          } else {
            realsvg = SVGUtils.generateSvgStringFromParts(parts);
          }

          const scale = SVGUtils.extractScale(frontPart.matrix);
          const cutline = SVGUtils.scaleSVGPath(frontPart.cuttingline, scale);
          const fonts = (await SVGUtils.fetchFontsFromSVG(realsvg)) as LoadedFont[];
          const imageUrl = await SVGUtils.createPng({
            svgString: realsvg,
            loadedFonts: fonts,
            width: 1080,
            height: 1080,
          });
          const svgBoundingBox = SVGUtils.getBoundingBox(realsvg);

          /* Checking front to prevent fetch for all parts. */
          const whiteLayerMasks = side == 'front' ? await fetchAndProcessMasks(productItem) : [];

          const allMasksAreColorMode = whiteLayerMasks.every(
            (mask) => mask.mode == WhiteLayerMaskMode.Color,
          );

          const baseProps: PreviewBaseProps = {
            patternScale: patternScale,
            sticker: {
              width: 0,
              height: 0,
              pathWidth: 0,
              pathHeight: 0,
              original: {
                height: 0,
                width: 0,
                originOffset: { x: 0, y: 0 },
                path: '',
                pathHeight: 0,
                pathWidth: 0,
              },
              previewImage: '',
              path: '',
              whiteLayerMasks: [],
            },
            laminate: LaminateType.Glossy,
            material: materialOptions[0],
            maskBackground: WhiteLayerMaskMode.Mix,
            useImageAlphaAsWhitelayer:
              productItem?.attributes?.white_layer == 'alpha' || allMasksAreColorMode,
          };

          return [
            side,
            {
              ...baseProps,
              sticker: {
                path: cutline,
                width: svgBoundingBox?.xMax,
                height: svgBoundingBox?.yMax,
                previewImage: imageUrl,
                whiteLayerMasks: whiteLayerMasks,
              },
              laminate:
                (productItem.attributes['laminate']?.toString() as LaminateType) ??
                LaminateType.Uncoated,
              material: {
                ...baseProps.material,
                type: productItem?.attributes['material'] as MaterialName,
                metallicOpacity: metallicOpacity,
                isMetal: materialSettings?.isMetal ?? false,
                laminateSettings: {
                  enabled: materialSettings?.laminateSettings.enabled ?? true,
                  laminateOpacity: materialSettings?.laminateSettings.laminateOpacity,
                },
              },
            },
          ];
        },
      ),
    );
  };

  type PreviewData =
    | { type: 'livePreview'; value: Awaited<ReturnType<typeof getLivePreviewProps>> }
    | { type: 'legacyPreview'; base64Image: string }
    | null;

  const previewDataCache = $state(new Map<string, PreviewData>());

  const getPreviewData = async (
    customStickerPromise: ReturnType<CheckoutSdk['getCustomSticker']>,
    adminPreview: boolean = false,
  ): Promise<PreviewData> => {
    const result = await customStickerPromise;
    if (!result) {
      return null;
    }
    const cacheKey = `${result.id}-${result.version}`;

    const cached = previewDataCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const simplePreview = await getSimplePreview(result, adminPreview).catch(() => undefined);

    const returnValue: PreviewData =
      simplePreview !== undefined && !simplePreview.livePreviewAvailable
        ? {
            type: 'legacyPreview',
            base64Image: simplePreview.preview,
          }
        : {
            type: 'livePreview',
            value: await getLivePreviewProps(
              result.productItem as ProductItemInterface,
              result.parts as Array<Part>,
            ),
          };

    previewDataCache.set(cacheKey, returnValue);

    return returnValue;
  };

  let stickerPreviewContext = $state({
    STICKERAPP_API_URL: '',
    ATLAS_ASSET_BASE_URL: '',
  });
</script>

<script lang="ts">
  import LoadingDots from '@athena-lib/Icons/LoadingDots.svelte';
  import { usePromise } from '@athena-lib/usePromise.svelte';
  import { BackgroundOption } from '@athena-lib/BackgroundSelector';
  import type { CheckoutSdk } from '@athena-lib/Checkout/utils';
  import type { CustomStickerWithId } from '@stickerapp-org/checkout-sdk';
  import type { Part, SvgPreviewProps } from '@athena-lib/SvgPreview/types';
  import { materialOptions, SVGUtils, type StickerPreviewContext } from '@athena-lib/LivePreview';
  import { getContext } from 'svelte';
  import { Type } from '@sinclair/typebox';
  import { Value } from '@sinclair/typebox/value';
  import clsx from 'clsx';
  import type { ProductItemInterface } from '@athena-lib/ProductWizard/product/model/ProductItemInterface';
  import type { LoadedFont, MaterialName, PreviewBaseProps, ProductItemDimensions } from '../types';
  import { StickerPreviewAnimationMode, WhiteLayerMaskMode, type WhiteLayerMask } from '../types';
  import { LaminateType } from '../types';

  import StickerPreview from './StickerPreview.svelte';

  const {
    customStickerPromise,
    animationMode = StickerPreviewAnimationMode.None,
    side = 'front',
    adminPreview = false,
    showMeasures = false,
    measures,
    class: className,
  }: {
    customStickerPromise: ReturnType<CheckoutSdk['getCustomSticker']>;
    animationMode?: SvgPreviewProps['animationMode'];
    side?: 'front' | 'back';
    adminPreview?: boolean;
    showMeasures?: boolean;
    class?: string;
    measures?: ProductItemDimensions;
  } = $props();

  let urlcontext = getContext<StickerPreviewContext>('stickerPreviewContext');
  if (urlcontext) {
    stickerPreviewContext = urlcontext;
  }

  const patternScale = 0.5;

  const awaitedPromise = usePromise(() => {
    return getPreviewData(customStickerPromise, adminPreview);
  });

  const previewProps: undefined | string | PreviewBaseProps = $derived.by(() => {
    if (awaitedPromise.value === undefined) {
      return undefined;
    }
    if (awaitedPromise.value?.type === 'livePreview') {
      return awaitedPromise.value.value.find((item) => item?.[0] === side)?.[1];
    }
    return awaitedPromise.value?.base64Image;
  });
</script>

{#if awaitedPromise.type == 'loading' && awaitedPromise.value === undefined}
  <LoadingDots sizeClass="h-12 w-12"></LoadingDots>
{/if}
{#if typeof previewProps === 'string'}
  <img
    class="max-h-full max-w-full object-contain"
    src={`data:image/png;base64, ${previewProps}`}
    alt=""
  />
{/if}
{#if typeof previewProps === 'object'}
  <StickerPreview
    className={clsx(className, 'p-2')}
    visible={!!awaitedPromise.value}
    staticUrl={stickerPreviewContext.ATLAS_ASSET_BASE_URL}
    isLoading={!awaitedPromise.value}
    baseProps={previewProps}
    productItemDimensions={measures}
    settings={{
      laminateLayerEnabled:
        side !== 'back' && (previewProps.material?.laminateSettings.enabled ?? true),
      patternScale: patternScale,
      lightweight: true,
      animationMode: animationMode ?? StickerPreviewAnimationMode.None,
      effectLayerEnabled: !previewProps.material?.type.includes('white') && side !== 'back',
      holoIndex: 2,
      showFps: false,
      backgroundOption: BackgroundOption.Light,
      fullSize: true,
      adminPreview,
      showMeasures,
    }}
    wiggle={true}
  ></StickerPreview>
{/if}
